import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import onlineOrderingService from "../services/onlineOrderingService";

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastFetchedAt: null,
};

export const fetchCustomerNotifications = createAsyncThunk(
  "customerNotification/fetchCustomerNotifications",
  async ({ restaurantSlug, locationId = null, pageNumber = 1, pageSize = 20 }, { rejectWithValue }) => {
    try {
      const data = await onlineOrderingService.getCustomerNotifications(
        restaurantSlug,
        pageNumber,
        pageSize,
        locationId
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to load notifications");
    }
  }
);

export const fetchCustomerUnreadCount = createAsyncThunk(
  "customerNotification/fetchCustomerUnreadCount",
  async ({ restaurantSlug, locationId = null }, { rejectWithValue }) => {
    try {
      const unreadCount = await onlineOrderingService.getCustomerUnreadNotificationCount(restaurantSlug, locationId);
      return unreadCount;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to load unread count");
    }
  }
);

export const markCustomerNotificationRead = createAsyncThunk(
  "customerNotification/markCustomerNotificationRead",
  async ({ restaurantSlug, locationId = null, notificationId }, { rejectWithValue }) => {
    try {
      const success = await onlineOrderingService.markCustomerNotificationRead(restaurantSlug, notificationId, locationId);
      if (!success) {
        return rejectWithValue("Failed to mark notification as read");
      }
      return notificationId;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to mark notification as read");
    }
  }
);

export const markAllCustomerNotificationsRead = createAsyncThunk(
  "customerNotification/markAllCustomerNotificationsRead",
  async ({ restaurantSlug, locationId = null }, { rejectWithValue }) => {
    try {
      const updatedCount = await onlineOrderingService.markAllCustomerNotificationsRead(restaurantSlug, locationId);
      return updatedCount;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to mark all notifications as read");
    }
  }
);

const customerNotificationSlice = createSlice({
  name: "customerNotification",
  initialState,
  reducers: {
    clearCustomerNotificationsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload) ? action.payload : [];
        state.unreadCount = state.items.filter((n) => !(n?.isRead ?? n?.IsRead ?? false)).length;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchCustomerNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load notifications";
      })
      .addCase(fetchCustomerUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = Number(action.payload || 0);
      })
      .addCase(markCustomerNotificationRead.fulfilled, (state, action) => {
        const targetId = Number(action.payload);
        state.items = state.items.map((item) => {
          const itemId = Number(item?.notificationID ?? item?.NotificationID ?? item?.id ?? item?.ID ?? 0);
          if (itemId === targetId) {
            return { ...item, isRead: true, IsRead: true };
          }
          return item;
        });
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(markAllCustomerNotificationsRead.fulfilled, (state) => {
        state.items = state.items.map((item) => ({ ...item, isRead: true, IsRead: true }));
        state.unreadCount = 0;
      });
  },
});

export const { clearCustomerNotificationsState } = customerNotificationSlice.actions;
export const selectCustomerNotifications = (state) => state.customerNotification?.items ?? [];
export const selectCustomerUnreadNotifications = (state) => state.customerNotification?.unreadCount ?? 0;
export const selectCustomerNotificationsLoading = (state) => state.customerNotification?.loading ?? false;

export default customerNotificationSlice.reducer;
